import { useState } from 'react';
import { Plus } from 'lucide-react';
import { VendorTable } from './VendorTable';
import { VendorForm } from './VendorForm';
import type { Vendor } from '@/types/vendor';
import { getStoredVendors, addStoredVendor, deleteStoredVendor, updateStoredVendor } from '../../../../utils/storage';

export function VendorManagement() {
  const [vendors, setVendors] = useState<Vendor[]>(() => getStoredVendors());
  const [showVendorForm, setShowVendorForm] = useState(false);
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);

  const handleAddVendor = (newVendor: Omit<Vendor, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'>) => {
    const vendor = addStoredVendor({
      ...newVendor,
      id: crypto.randomUUID(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: 'current-user',
      updatedBy: 'current-user',
    });
    setVendors(prev => [...prev, vendor]);
    setShowVendorForm(false);
  };

  const handleEditVendor = (vendor: Vendor) => {
    setEditingVendor(vendor);
    setShowVendorForm(true);
  };

  const handleUpdateVendor = (updatedVendor: Omit<Vendor, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'>) => {
    if (editingVendor) {
      const vendor = updateStoredVendor({
        ...updatedVendor,
        id: editingVendor.id,
        createdAt: editingVendor.createdAt,
        createdBy: editingVendor.createdBy,
        updatedAt: new Date().toISOString(),
        updatedBy: 'current-user',
      });
      setVendors(prev => prev.map(v => v.id === editingVendor.id ? vendor as Vendor : v as Vendor));
      setShowVendorForm(false);
      setEditingVendor(null);
    }
  };

  const handleDeleteVendor = (vendorId: string) => {
    deleteStoredVendor(vendorId);
    setVendors(prev => prev.filter(v => v.id !== vendorId));
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Vendor Management</h2>
        <button
          type="button"
          onClick={() => {
            setEditingVendor(null);
            setShowVendorForm(true);
          }}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <Plus className="h-5 w-5 mr-2" />
          Add New Vendor
        </button>
      </div>

      <VendorTable
        vendors={vendors}
        onEdit={handleEditVendor}
        onDelete={handleDeleteVendor}
      />

      {showVendorForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="max-w-2xl w-full bg-white rounded-lg shadow-md">
            <VendorForm
              vendor={editingVendor}
              onSubmit={editingVendor ? handleUpdateVendor : handleAddVendor}
              onCancel={() => {
                setShowVendorForm(false);
                setEditingVendor(null);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}